import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, CircularProgress, Alert, TextField,
} from '@mui/material';
import { Refresh, AccessTime, Euro, ShoppingCart, Schedule } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebaseConfig';

interface Shift {
  id: string;
  user_email: string;
  user_role: string;
  start_time: string | null;
  end_time: string | null;
  cash_opening: number;
  cash_closing: number | null;
  cash_expected: number | null;
  cash_variance: number | null;
  orders_count: number;
  total_sales: number;
  total_cash: number;
  total_card: number;
  total_paypal: number;
  refunds_count: number;
  refunds_amount: number;
  cancellations_count: number;
  uncounted_close?: boolean;
}

const ShiftsPage: React.FC = () => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterUser, setFilterUser] = useState('');
  // Default range = last 14 days. Admin can widen/shorten.
  const today = new Date();
  const twoWeeksAgo = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
  const [filterFrom, setFilterFrom] = useState<string>(twoWeeksAgo.toISOString().slice(0, 10));
  const [filterTo, setFilterTo] = useState<string>(today.toISOString().slice(0, 10));

  const loadShifts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getShifts = httpsCallable(functions, 'getShifts');
      const payload: any = { limit: 200 };
      if (filterUser) payload.user_email = filterUser;
      if (filterFrom) payload.from = new Date(filterFrom + 'T00:00:00').toISOString();
      if (filterTo) payload.to = new Date(filterTo + 'T23:59:59').toISOString();
      const result = await getShifts(payload);
      const data = result.data as any;
      const rawShifts: Shift[] = data.shifts || [];

      // BUG FIX: closeShift() is what writes orders_count/total_sales/etc onto
      // pos_shifts. For ACTIVE shifts (end_time === null) those fields stay at 0,
      // which made the admin page look like no one was doing anything. Aggregate
      // live from the `orders` collection for every active shift so the admin sees
      // real-time sales per cashier before they clock out.
      const { collection, query, where, getDocs, Timestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const enriched = await Promise.all(rawShifts.map(async (s) => {
        if (s.end_time) return s; // already finalised by closeShift
        if (!s.user_email || !s.start_time) return s;
        try {
          const startTs = Timestamp.fromDate(new Date(s.start_time));
          const snap = await getDocs(query(
            collection(db, 'orders'),
            where('created_by_email', '==', s.user_email),
            where('created_at', '>=', startTs),
          ));
          let ordersCount = 0, totalSales = 0, totalCash = 0, totalCard = 0, totalPaypal = 0;
          let refundsCount = 0, refundsAmount = 0, cancellationsCount = 0;
          snap.forEach((d) => {
            const o = d.data() as any;
            const status = (o.status || '').toLowerCase();
            if (status === 'cancelled') { cancellationsCount++; return; }
            const total = parseFloat(o.total || 0);
            if (isNaN(total)) return;
            if (o.refund_amount && parseFloat(o.refund_amount) > 0) {
              refundsCount++;
              refundsAmount += parseFloat(o.refund_amount);
            }
            if (status === 'completed' || status === 'delivered' || o.payment_status === 'completed') {
              ordersCount++;
              totalSales += total;
              const pm = (o.payment_method || '').toLowerCase();
              if (pm === 'cash') totalCash += total;
              else if (pm === 'card' || pm === 'ec') totalCard += total;
              else if (pm === 'paypal') totalPaypal += total;
            }
          });
          const expected = (s.cash_opening || 0) + totalCash - refundsAmount;
          return {
            ...s,
            orders_count: ordersCount,
            total_sales: Math.round(totalSales * 100) / 100,
            total_cash: Math.round(totalCash * 100) / 100,
            total_card: Math.round(totalCard * 100) / 100,
            total_paypal: Math.round(totalPaypal * 100) / 100,
            refunds_count: refundsCount,
            refunds_amount: Math.round(refundsAmount * 100) / 100,
            cancellations_count: cancellationsCount,
            cash_expected: Math.round(expected * 100) / 100,
          } as Shift;
        } catch (e) {
          console.warn('[SHIFTS] live aggregate failed for', s.user_email, e);
          return s;
        }
      }));
      setShifts(enriched);
    } catch (err: any) {
      console.error('Shifts fetch failed:', err);
      setError(err.message || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterFrom, filterTo]);

  useEffect(() => { loadShifts(); }, [loadShifts]);

  const formatDuration = (start: string | null, end: string | null) => {
    if (!start) return '—';
    const s = new Date(start);
    const e = end ? new Date(end) : new Date();
    const ms = e.getTime() - s.getTime();
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  const formatTime = (t: string | null) => {
    if (!t) return '—';
    return new Date(t).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const totalSales = shifts.reduce((s, sh) => s + (sh.total_sales || 0), 0);
  const totalOrders = shifts.reduce((s, sh) => s + (sh.orders_count || 0), 0);
  const activeShifts = shifts.filter(s => !s.end_time).length;
  // Variance flags: anything over €5 off (either direction) is suspicious.
  const flaggedShifts = shifts.filter(s => s.cash_variance !== null && s.cash_variance !== undefined && Math.abs(s.cash_variance) > 5);
  const totalVariance = shifts.reduce((sum, sh) => sum + (sh.cash_variance || 0), 0);

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#e0e0e0', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Schedule sx={{ color: '#00C896', fontSize: 32 }} />
              Staff Shifts
            </Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem', mt: 0.5 }}>
              Auto-tracked when staff log in / out of POS — sales, hours, cash variance
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Refresh />} onClick={loadShifts} disabled={loading}
            sx={{ background: '#00C896', color: '#1a1d2e', fontWeight: 700, '&:hover': { background: '#7bc012' } }}>
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#1a1d2e', border: '1px solid #2a2d42', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Filter by user email" size="small" value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            placeholder="e.g. cashier@..."
            sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' } }, '& .MuiInputLabel-root': { color: '#9ca3af' } }}
          />
          <TextField
            label="From" type="date" size="small" value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' } }, '& .MuiInputLabel-root': { color: '#9ca3af' },
              '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.7)' } }}
          />
          <TextField
            label="To" type="date" size="small" value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' } }, '& .MuiInputLabel-root': { color: '#9ca3af' },
              '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.7)' } }}
          />
          <Button size="small" variant="outlined" onClick={() => { setFilterUser(''); setFilterFrom(twoWeeksAgo.toISOString().slice(0,10)); setFilterTo(today.toISOString().slice(0,10)); }}
            sx={{ color: '#9ca3af', borderColor: '#3a3d52', textTransform: 'none' }}>
            Reset
          </Button>
        </Paper>

        {/* Variance alert banner — shows when any shift in range is off by more than €5.
            Click-through filter (sets user to flagged user) not included to keep this simple. */}
        {flaggedShifts.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(255,152,0,0.08)', border: '1px solid rgba(255,152,0,0.3)', color: '#FFB74D' }}>
            <strong>{flaggedShifts.length}</strong> shift{flaggedShifts.length > 1 ? 's' : ''} in this range have a cash variance over €5.
            Total net variance: <strong>€{totalVariance.toFixed(2)}</strong>.
            Review the red rows below and talk to the cashiers involved.
          </Alert>
        )}

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ px: 2, py: 1.5, bgcolor: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.2)', minWidth: 150 }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600 }}>ACTIVE SHIFTS</Typography>
            <Typography sx={{ color: '#4CAF50', fontSize: '1.6rem', fontWeight: 700 }}>{activeShifts}</Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1.5, bgcolor: 'rgba(0, 200, 150,0.08)', border: '1px solid rgba(0, 200, 150,0.2)', minWidth: 150 }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600 }}>TOTAL SALES</Typography>
            <Typography sx={{ color: '#00C896', fontSize: '1.6rem', fontWeight: 700 }}>€{totalSales.toFixed(2)}</Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1.5, bgcolor: 'rgba(33,150,243,0.08)', border: '1px solid rgba(33,150,243,0.2)', minWidth: 150 }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600 }}>TOTAL ORDERS</Typography>
            <Typography sx={{ color: '#64B5F6', fontSize: '1.6rem', fontWeight: 700 }}>{totalOrders}</Typography>
          </Paper>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper} sx={{ bgcolor: '#1a1d2e', border: '1px solid #2a2d42' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Staff</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Started</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Ended</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }}>Duration</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }} align="right">Orders</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }} align="right">Sales</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }} align="right">Cash</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }} align="right">Card</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }} align="right">Refunds</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }} align="right">Cancels</TableCell>
                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontWeight: 700 }} align="right">Variance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, borderBottom: 'none' }}><CircularProgress sx={{ color: '#00C896' }} /></TableCell></TableRow>
              ) : shifts.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6, color: '#6b7280', borderBottom: 'none' }}>No shifts yet.</TableCell></TableRow>
              ) : shifts.map((s) => (
                <TableRow key={s.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 200, 150,0.04)' } }}>
                  <TableCell sx={{ borderBottom: '1px solid #2a2d42' }}>
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: '0.8rem' }}>{s.user_email}</Typography>
                    <Chip label={s.user_role} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(0, 200, 150,0.1)', color: '#00C896' }} />
                  </TableCell>
                  <TableCell sx={{ color: '#9ca3af', fontSize: '0.75rem', borderBottom: '1px solid #2a2d42' }}>{formatTime(s.start_time)}</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #2a2d42' }}>
                    {s.end_time ? <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>{formatTime(s.end_time)}</Typography>
                                : <Chip label="🟢 ACTIVE" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#4CAF50', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />}
                  </TableCell>
                  <TableCell sx={{ color: '#FFC107', fontSize: '0.78rem', fontWeight: 600, borderBottom: '1px solid #2a2d42' }}>{formatDuration(s.start_time, s.end_time)}</TableCell>
                  <TableCell sx={{ color: '#64B5F6', fontWeight: 600, fontSize: '0.85rem', borderBottom: '1px solid #2a2d42' }} align="right">{s.orders_count || 0}</TableCell>
                  <TableCell sx={{ color: '#00C896', fontWeight: 700, fontSize: '0.85rem', borderBottom: '1px solid #2a2d42' }} align="right">€{(s.total_sales || 0).toFixed(2)}</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontSize: '0.78rem', borderBottom: '1px solid #2a2d42' }} align="right">€{(s.total_cash || 0).toFixed(2)}</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontSize: '0.78rem', borderBottom: '1px solid #2a2d42' }} align="right">€{(s.total_card || 0).toFixed(2)}</TableCell>
                  <TableCell sx={{ color: s.refunds_count > 0 ? '#FF9800' : '#666', fontSize: '0.78rem', borderBottom: '1px solid #2a2d42' }} align="right">
                    {s.refunds_count > 0 ? `${s.refunds_count} (€${(s.refunds_amount || 0).toFixed(2)})` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: s.cancellations_count > 0 ? '#ef5350' : '#666', fontSize: '0.78rem', borderBottom: '1px solid #2a2d42' }} align="right">{s.cancellations_count || 0}</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #2a2d42' }} align="right">
                    {s.uncounted_close
                      ? <Chip label="⚠ UNCOUNTED" size="small" sx={{ bgcolor: 'rgba(255,152,0,0.18)', color: '#FFB74D', fontWeight: 700, fontSize: '0.65rem', height: 22 }} title="Cashier closed shift without counting the drawer" />
                      : s.cash_variance === null || s.cash_variance === undefined ? <span style={{ color: '#666' }}>—</span> :
                        Math.abs(s.cash_variance) > 5
                          ? <Chip label={`€${s.cash_variance > 0 ? '+' : ''}${s.cash_variance.toFixed(2)}`} size="small" sx={{ bgcolor: 'rgba(239,83,80,0.15)', color: '#ef5350', fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
                          : <Typography sx={{ color: '#4CAF50', fontSize: '0.8rem' }}>€{s.cash_variance.toFixed(2)}</Typography>
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </AdminLayout>
  );
};

export default ShiftsPage;
